import { formatINR, INSTAGRAM_HANDLE, STORE_NAME, STORE_TAGLINE, type SareeDTO } from '@neyara/shared';
import { useGSAP } from '@gsap/react';
import { useMemo, useRef, type CSSProperties } from 'react';
import { Link } from 'react-router';
import { ArrowIcon, WhatsAppIcon } from '../components/icons';
import { Lotus, LotusDivider } from '../components/lotus';
import { SareeCard } from '../components/saree-card';
import { SareeImage } from '../components/saree-image';
import { SectionHeading } from '../components/section-heading';
import { SEAL_TONES, type SealTone } from '../components/seal';
import { useCategories, useFacets, useSarees } from '../lib/queries';
import { gsap, MOTION_QUERY } from '../lib/scroll';
import { useWhatsApp } from '../lib/whatsapp';

export function HomePage() {
  // The server samples 8 at random from all featured sarees; the hero takes two of them.
  const featured = useSarees({ featured: true, random: true, pageSize: 8 });
  const newest = useSarees({ sort: 'newest', pageSize: 8 });
  const hanging = useMemo(() => pickHanging(featured.data?.items, newest.data?.items), [featured.data, newest.data]);

  return (
    <>
      <title>{`${STORE_NAME} - ${STORE_TAGLINE}`}</title>
      <Hero hanging={hanging} />
      <Weaves />
      {featured.data && featured.data.items.length > 0 && (
        <ProductSection
          id="featured"
          title="Featured"
          sarees={featured.data.items}
          more={{ to: '/sarees?featured=true', label: 'All featured sarees' }}
        />
      )}
      <NewArrivals sarees={newest.data?.items} loading={newest.isPending} />
      <Occasions />
    </>
  );
}

// ---------- hero ----------

/** Two in-stock sarees for the hero: from the random featured sample, topped up with newest if needed. */
function pickHanging(featured: SareeDTO[] = [], newest: SareeDTO[] = []): SareeDTO[] {
  const picks = featured.filter((s) => s.stock > 0).slice(0, 2);
  for (const s of newest) {
    if (picks.length === 2) break;
    if (s.stock > 0 && !picks.some((p) => p.id === s.id)) picks.push(s);
  }
  return picks;
}

function Hero({ hanging }: { hanging: SareeDTO[] }) {
  const whatsapp = useWhatsApp();
  const [left, right] = hanging;
  const copy = useRef<HTMLDivElement>(null);

  // The tagline unfolds once on arrival; everything is visible by default without JS or with reduced motion.
  useGSAP(
    () => {
      const mm = gsap.matchMedia();
      mm.add(MOTION_QUERY, () => {
        gsap.from('[data-hero-step]', {
          autoAlpha: 0,
          y: 34,
          duration: 1.2,
          ease: 'expo.out',
          stagger: 0.12,
          delay: 0.1,
        });
      });
      return () => mm.revert();
    },
    { scope: copy },
  );

  return (
    <section className="tissue relative overflow-hidden" aria-labelledby="hero-title">
      {left && <HangingTag saree={left} side="left" />}
      {right && <HangingTag saree={right} side="right" />}

      <div
        ref={copy}
        data-speed="clamp(0.88)"
        className="relative mx-auto flex max-w-4xl flex-col items-center justify-center px-5 pt-14 pb-16 text-center sm:min-h-[calc(100svh-5rem-4rem)] sm:py-20"
      >
        <Lotus data-hero-step className="h-9 w-14 text-gold-deep sm:h-11 sm:w-16" />
        <h1
          id="hero-title"
          className="mt-6 font-script text-[clamp(3.25rem,10vw,6rem)] leading-[0.95] font-medium tracking-[-0.01em] text-maroon italic"
        >
          <span data-hero-step className="block">
            Timeless Drapes.
          </span>
          <span data-hero-step className="block sm:translate-x-[0.9em]">
            Eternal You.
          </span>
        </h1>
        <div data-hero-step>
          <LotusDivider className="mt-8" />
        </div>
        <p data-hero-step className="mt-6 max-w-md text-[1.1rem] text-ink-soft sm:text-lg">
          Handloom sarees, bought directly from the weavers who make them.
        </p>
        <div
          data-hero-step
          className="mt-9 flex w-full flex-col items-center justify-center gap-3 sm:w-auto sm:flex-row sm:gap-4"
        >
          <Link to="/sarees" className="btn btn-maroon w-full sm:w-auto">
            Browse the sarees <ArrowIcon size={18} />
          </Link>
          {whatsapp && (
            <a
              href={whatsapp.chat()}
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-outline w-full sm:w-auto"
            >
              <WhatsAppIcon size={19} /> Ask us on WhatsApp
            </a>
          )}
        </div>
      </div>
    </section>
  );
}

/** A real catalog saree hanging from the header rail on a gold thread (wide screens only). */
function HangingTag({ saree, side }: { saree: SareeDTO; side: 'left' | 'right' }) {
  const isLeft = side === 'left';
  return (
    // Outer layer drifts slower than the page (parallax); inner link keeps its settling sway.
    <div
      data-speed={isLeft ? 'clamp(0.8)' : 'clamp(0.7)'}
      className={`absolute top-0 hidden w-52 xl:block ${isLeft ? 'left-[7%]' : 'right-[7%]'}`}
    >
      <Link
        to={`/sarees/${saree.slug}`}
        aria-label={`${saree.name}, ${formatINR(saree.price)}`}
        style={
          {
            '--tag-from': isLeft ? '9deg' : '-9deg',
            '--tag-to': isLeft ? '-3deg' : '3deg',
          } as CSSProperties
        }
        className="group block origin-top animate-[tag-settle_2.4s_var(--ease-out-expo)_both] [transform:rotate(var(--tag-to))]"
      >
        <span aria-hidden="true" className={`mx-auto block w-px bg-gold ${isLeft ? 'h-24' : 'h-36'}`} />
        <span className="tag-drop block transition-transform duration-500 group-hover:-translate-y-1">
          <span className="tag-shape block bg-champagne p-3 pt-7">
            <span
              aria-hidden="true"
              className="absolute top-2.5 left-1/2 size-2.5 -translate-x-1/2 rounded-full bg-tissue ring-1 ring-champagne-deep ring-inset"
            />
            <SareeImage image={saree.images[0]} name={saree.name} className="aspect-[4/5] w-full" />
            <span className="mt-3 block px-1 text-left">
              <span className="block font-display text-[0.72rem] leading-snug tracking-[0.08em] text-charcoal uppercase">
                {saree.name}
              </span>
              {saree.category && (
                <span className="block font-script text-base leading-tight text-ink-soft italic">
                  {saree.category.name}
                </span>
              )}
              <span className="tabular mt-1 block text-sm text-maroon">{formatINR(saree.price)}</span>
            </span>
          </span>
        </span>
      </Link>
    </div>
  );
}

// ---------- weaves ----------

function Weaves() {
  const { data: categories } = useCategories();
  if (!categories?.length) return null;

  return (
    <section id="weaves" aria-labelledby="weaves-title" className="on-maroon scroll-mt-20 bg-maroon py-20 sm:py-24">
      <div className="mx-auto max-w-7xl px-4 sm:px-8">
        <SectionHeading id="weaves-title" tone="light">
          Shop by Weave
        </SectionHeading>
        {/* One row on desktop when it fits (no orphaned label), two compact columns on phones. */}
        <ul
          style={{ '--weave-cols': categories.length <= 5 ? categories.length : 4 } as CSSProperties}
          className="mt-10 grid grid-cols-2 gap-3 sm:mt-12 sm:gap-5 lg:grid-cols-[repeat(var(--weave-cols),minmax(0,1fr))]"
        >
          {categories.map((category, i) => (
            <li
              key={category.id}
              data-reveal
              className={categories.length % 2 === 1 && i === categories.length - 1 ? 'col-span-2 lg:col-span-1' : ''}
            >
              <Link
                to={`/sarees?category=${category.slug}`}
                className="woven-label group flex h-full flex-col items-center bg-champagne px-3 py-5 text-center sm:px-6 sm:py-7 transition-transform duration-500 ease-(--ease-out-expo) hover:-translate-y-1"
              >
                <span className="font-display text-[0.92rem] font-medium tracking-[0.1em] text-maroon uppercase sm:text-lg sm:tracking-[0.16em]">
                  {category.name}
                </span>
                {category.description && (
                  <span className="mt-2 line-clamp-2 hidden font-script text-[1.15rem] leading-snug text-ink-soft italic sm:block">
                    {category.description}
                  </span>
                )}
                <span className="mt-auto inline-flex items-center gap-2 pt-3 text-[0.68rem] sm:pt-4 sm:text-[0.72rem] font-medium tracking-[0.18em] text-maroon uppercase">
                  View sarees
                  <ArrowIcon size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
                </span>
              </Link>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

// ---------- product grids ----------

function ProductSection({
  id,
  title,
  sarees,
  more,
}: {
  id: string;
  title: string;
  sarees: SareeDTO[];
  more: { to: string; label: string };
}) {
  return (
    <section aria-labelledby={`${id}-title`} className="tissue py-20 sm:py-24">
      <div className="mx-auto max-w-7xl px-4 sm:px-8">
        <SectionHeading id={`${id}-title`}>{title}</SectionHeading>
        <div className="mt-12 grid grid-cols-2 gap-x-4 gap-y-12 sm:gap-x-8 lg:grid-cols-4">
          {sarees.map((saree) => (
            <SareeCard key={saree.id} saree={saree} />
          ))}
        </div>
        <div className="mt-14 text-center">
          <Link to={more.to} className="btn btn-outline">
            {more.label} <ArrowIcon size={18} />
          </Link>
        </div>
      </div>
    </section>
  );
}

function NewArrivals({ sarees, loading }: { sarees: SareeDTO[] | undefined; loading: boolean }) {
  if (loading) return <GridSkeleton />;
  if (sarees && sarees.length > 0) {
    return (
      <ProductSection id="new" title="New Arrivals" sarees={sarees} more={{ to: '/sarees', label: 'Browse all sarees' }} />
    );
  }
  return (
    <section className="tissue py-24 text-center" aria-label="New arrivals">
      <div className="mx-auto max-w-xl px-6">
        <Lotus className="mx-auto h-10 w-16 text-gold-deep" />
        <p className="mt-5 font-script text-3xl text-maroon italic">New sarees are on their way.</p>
        <p className="mt-3 text-ink-soft">
          Follow{' '}
          <a
            href={`https://instagram.com/${INSTAGRAM_HANDLE}`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-maroon underline"
          >
            @{INSTAGRAM_HANDLE}
          </a>{' '}
          on Instagram to see them first.
        </p>
      </div>
    </section>
  );
}

function GridSkeleton() {
  return (
    <div className="tissue py-24" aria-busy="true" aria-label="Loading sarees">
      <div className="mx-auto grid max-w-7xl grid-cols-2 gap-x-4 gap-y-12 px-4 sm:gap-x-8 sm:px-8 lg:grid-cols-4">
        {Array.from({ length: 4 }, (_, i) => (
          <div key={i} className="aspect-[3/4] animate-pulse bg-champagne/70" />
        ))}
      </div>
    </div>
  );
}

// ---------- occasions ----------

const OCCASION_TONES: SealTone[] = ['maroon', 'olive', 'pink', 'gold', 'charcoal'];

function Occasions() {
  const { data: facets } = useFacets();
  const occasions = facets?.occasions ?? [];
  if (occasions.length === 0) return null;

  return (
    <section aria-labelledby="occasions-title" className="bg-champagne py-20 sm:py-24">
      <div className="mx-auto max-w-6xl px-4 sm:px-8">
        <SectionHeading id="occasions-title">Shop by Occasion</SectionHeading>
        <ul className="mt-12 flex flex-wrap justify-center gap-5 sm:gap-8">
          {occasions.map((occasion, i) => {
            const tone = OCCASION_TONES[i % OCCASION_TONES.length]!;
            return (
              <li key={occasion} data-reveal="seal">
                <Link
                  to={`/sarees?occasion=${encodeURIComponent(occasion)}`}
                  className={`grid size-32 place-items-center rounded-full p-3 text-center shadow-(--shadow-seal) ring-1 ring-inset transition-transform duration-500 ease-(--ease-out-expo) hover:-translate-y-1 hover:-rotate-6 sm:size-40 ${SEAL_TONES[tone]}`}
                >
                  <span className="flex flex-col items-center gap-2">
                    <Lotus className="h-4 w-7 opacity-80" />
                    <span className="font-display text-[0.8rem] font-medium tracking-[0.16em] uppercase sm:text-sm">
                      {occasion}
                    </span>
                  </span>
                </Link>
              </li>
            );
          })}
        </ul>
      </div>
    </section>
  );
}
